import React from "react";
import { Paper, Box, Typography, Stack } from "@mui/material";
import { fig } from "../../theme";
import { US_VIEWBOX, US_STATE_PATHS } from "./usStatePaths";

/*
 * FIG-themed, dependency-free presentational charts for the admin
 * Site Activity dashboard. Everything is hand-rolled MUI + inline SVG so we
 * add zero npm dependencies. All components render gracefully with empty data.
 */

// Categorical palette — used by DonutChart (and available for reuse).
export const PALETTE = [
    fig.teal,
    fig.green,
    fig.orange,
    fig.tealDark,
    fig.greenDark,
    "#0f8b93",
    "#a6db83",
    "#ffb07d",
    fig.inkSoft,
];

// Small helper — empty-state message block.
function EmptyState({ label = "No data yet" }) {
    return (
        <Box
            sx={{
                py: 4,
                textAlign: "center",
                color: "text.secondary",
                fontSize: "0.9rem",
            }}
        >
            {label}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// StatTile — a single KPI tile.
// ---------------------------------------------------------------------------
export function StatTile({ label, value, sublabel }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                height: "100%",
                border: (t) => `1px solid ${t.palette.divider}`,
                boxShadow: (t) => `0 6px 20px ${t.palette.action.hover}`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.5,
            }}
        >
            <Typography
                variant="overline"
                sx={{ color: "text.secondary", lineHeight: 1.2 }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    color: "primary.main",
                    fontWeight: 700,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.3rem", md: "1.55rem" },
                    wordBreak: "break-word",
                }}
            >
                {value}
            </Typography>
            {sublabel ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {sublabel}
                </Typography>
            ) : null}
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// BarList — horizontal bars scaled to the max count.
// ---------------------------------------------------------------------------
export function BarList({ data, unit }) {
    const rows = Array.isArray(data) ? data.slice(0, 8) : [];
    if (rows.length === 0) return <EmptyState />;

    const max = rows.reduce((m, r) => Math.max(m, Number(r.count) || 0), 0) || 1;
    const unitLabel = unit ? ` ${unit}` : "";

    return (
        <Stack spacing={1.25} sx={{ width: "100%" }} role="list">
            {rows.map((r, i) => {
                const count = Number(r.count) || 0;
                const pct = Math.max(2, Math.round((count / max) * 100));
                return (
                    <Box key={`${r.key}-${i}`} role="listitem">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                mb: 0.4,
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="body2"
                                title={String(r.key)}
                                sx={{
                                    color: "text.primary",
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                }}
                            >
                                {r.key}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}
                            >
                                {count.toLocaleString()}
                                {unitLabel}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                height: 8,
                                borderRadius: 999,
                                bgcolor: (t) => t.palette.action.hover,
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    borderRadius: 999,
                                    background: fig.gradient,
                                    transition: "width 400ms ease",
                                }}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );
}

// ---------------------------------------------------------------------------
// DonutChart — inline SVG donut + legend.
// ---------------------------------------------------------------------------
export function DonutChart({ data, size = 180 }) {
    const rows = (Array.isArray(data) ? data : []).filter(
        (r) => (Number(r.count) || 0) > 0
    );
    const total = rows.reduce((s, r) => s + (Number(r.count) || 0), 0);

    if (rows.length === 0 || total === 0) return <EmptyState />;

    const stroke = Math.max(14, Math.round(size * 0.16));
    const radius = (size - stroke) / 2;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;

    let offset = 0;
    const segments = rows.map((r, i) => {
        const value = Number(r.count) || 0;
        const frac = value / total;
        const dash = frac * circumference;
        const seg = {
            key: r.key,
            count: value,
            color: PALETTE[i % PALETTE.length],
            pct: Math.round(frac * 100),
            dashArray: `${dash} ${circumference - dash}`,
            dashOffset: -offset,
        };
        offset += dash;
        return seg;
    });

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2.5,
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    role="img"
                    aria-label="Distribution donut chart"
                >
                    <circle
                        cx={cx}
                        cy={cy}
                        r={radius}
                        fill="none"
                        stroke={fig.bg}
                        strokeWidth={stroke}
                    />
                    <g transform={`rotate(-90 ${cx} ${cy})`}>
                        {segments.map((s, i) => (
                            <circle
                                key={`${s.key}-${i}`}
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                stroke={s.color}
                                strokeWidth={stroke}
                                strokeDasharray={s.dashArray}
                                strokeDashoffset={s.dashOffset}
                            >
                                <title>{`${s.key}: ${s.count} (${s.pct}%)`}</title>
                            </circle>
                        ))}
                    </g>
                    <text
                        x={cx}
                        y={cy - 4}
                        textAnchor="middle"
                        style={{
                            fontSize: size * 0.2,
                            fontWeight: 700,
                            fill: fig.ink,
                        }}
                    >
                        {total.toLocaleString()}
                    </text>
                    <text
                        x={cx}
                        y={cy + size * 0.12}
                        textAnchor="middle"
                        style={{ fontSize: size * 0.08, fill: fig.inkSoft }}
                    >
                        total
                    </text>
                </svg>
            </Box>
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                {segments.map((s, i) => (
                    <Box
                        key={`${s.key}-legend-${i}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "3px",
                                bgcolor: s.color,
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            variant="body2"
                            title={String(s.key)}
                            sx={{
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {s.key}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", flexShrink: 0 }}
                        >
                            {s.count.toLocaleString()} · {s.pct}%
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// TrendLine — inline SVG line + area, responsive via viewBox.
// ---------------------------------------------------------------------------
export function TrendLine({ data, height = 220, showValues = false }) {
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return <EmptyState label="No trend data yet" />;

    // Fixed viewBox coordinate space; renders responsive at 100% width.
    const vbWidth = 800;
    const vbHeight = height;
    const padL = 8;
    const padR = 8;
    const padT = 16;
    const padB = 28;
    const innerW = vbWidth - padL - padR;
    const innerH = vbHeight - padT - padB;

    const maxCount = rows.reduce((m, r) => Math.max(m, Number(r.count) || 0), 0) || 1;
    const n = rows.length;

    const xFor = (i) => (n === 1 ? padL + innerW / 2 : padL + (i / (n - 1)) * innerW);
    const yFor = (c) => padT + innerH - ((Number(c) || 0) / maxCount) * innerH;

    const points = rows.map((r, i) => ({
        x: xFor(i),
        y: yFor(r.count),
        date: r.date,
        count: Number(r.count) || 0,
    }));

    // Smooth the line with Catmull-Rom-derived cubic bezier segments.
    const TENSION = 0.18;
    const curveSegments = points.slice(1).map((p2, idx) => {
        const i = idx + 1;
        const p0 = points[i - 2] || points[i - 1];
        const p1 = points[i - 1];
        const p3 = points[i + 1] || p2;
        const c1x = p1.x + (p2.x - p0.x) * TENSION;
        const c1y = p1.y + (p2.y - p0.y) * TENSION;
        const c2x = p2.x - (p3.x - p1.x) * TENSION;
        const c2y = p2.y - (p3.y - p1.y) * TENSION;
        return `C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
    });

    const start = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
    const linePath = curveSegments.length ? `${start} ${curveSegments.join(" ")}` : start;

    const baseY = padT + innerH;
    const areaPath = curveSegments.length
        ? `M ${points[0].x.toFixed(2)} ${baseY.toFixed(2)} L ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)} ` +
          curveSegments.join(" ") +
          ` L ${points[points.length - 1].x.toFixed(2)} ${baseY.toFixed(2)} Z`
        : "";

    // Sparse x-axis labels: aim for ~4-6 evenly spaced ticks.
    const maxLabels = 6;
    const step = Math.max(1, Math.ceil(n / maxLabels));
    const labelIdx = [];
    for (let i = 0; i < n; i += step) labelIdx.push(i);
    if (labelIdx[labelIdx.length - 1] !== n - 1) labelIdx.push(n - 1);

    const gradId = "sk12-trend-fill";

    return (
        <Box sx={{ width: "100%" }}>
            <svg
                width="100%"
                viewBox={`0 0 ${vbWidth} ${vbHeight}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Views over time trend line"
                style={{ display: "block", height: "auto" }}
            >
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={fig.green} stopOpacity="0.28" />
                        <stop offset="100%" stopColor={fig.green} stopOpacity="0.02" />
                    </linearGradient>
                </defs>

                <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
                <path
                    d={linePath}
                    fill="none"
                    stroke={fig.teal}
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />

                {points.map((p, i) => (
                    <g key={`pt-${i}`}>
                        <circle cx={p.x} cy={p.y} r="3.5" fill={fig.tealDark}>
                            <title>{`${p.date}: ${p.count}`}</title>
                        </circle>
                        {showValues && (
                            <text
                                x={p.x}
                                y={p.y - 9}
                                textAnchor="middle"
                                style={{ fontSize: 13, fontWeight: 700, fill: fig.ink }}
                            >
                                {p.count}
                            </text>
                        )}
                    </g>
                ))}

                {labelIdx.map((i) => {
                    const p = points[i];
                    const anchor =
                        i === 0 ? "start" : i === n - 1 ? "end" : "middle";
                    return (
                        <text
                            key={`lbl-${i}`}
                            x={p.x}
                            y={vbHeight - 8}
                            textAnchor={anchor}
                            style={{ fontSize: 12, fill: fig.inkSoft }}
                        >
                            {p.date}
                        </text>
                    );
                })}
            </svg>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// StateHeat — a "visual by state": heat-colored tiles, one per state, sized by
// nothing but colored by view intensity (deeper teal = more views). Geo comes
// back as full state names (ipapi region), so we badge with the abbreviation.
// ---------------------------------------------------------------------------
const STATE_ABBR = {
    Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
    Colorado: "CO", Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC",
    Florida: "FL", Georgia: "GA", Hawaii: "HI", Idaho: "ID", Illinois: "IL",
    Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY", Louisiana: "LA",
    Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
    Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
    "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH", Oklahoma: "OK",
    Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
    "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT",
    Virginia: "VA", Washington: "WA", "West Virginia": "WV", Wisconsin: "WI",
    Wyoming: "WY",
};

export function StateHeat({ data, maxTiles = 60 }) {
    const rows = (Array.isArray(data) ? data : [])
        .filter((r) => r.key && r.key !== "Unknown" && (Number(r.count) || 0) > 0)
        .slice(0, maxTiles);

    if (rows.length === 0) return <EmptyState label="No location data yet" />;

    const max = rows.reduce((m, r) => Math.max(m, Number(r.count) || 0), 0) || 1;

    return (
        <Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {rows.map((r) => {
                    const count = Number(r.count) || 0;
                    const intensity = 0.22 + 0.78 * (count / max); // 0.22..1
                    const abbr =
                        STATE_ABBR[r.key] || String(r.key).slice(0, 2).toUpperCase();
                    return (
                        <Box
                            key={r.key}
                            title={`${r.key}: ${count.toLocaleString()} views`}
                            sx={{
                                width: 62,
                                height: 58,
                                borderRadius: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1.1,
                                background: `rgba(30,190,200,${intensity.toFixed(3)})`,
                                border: "1px solid rgba(30,190,200,0.25)",
                                color: intensity > 0.58 ? "#fff" : fig.ink,
                            }}
                        >
                            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                                {abbr}
                            </Typography>
                            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                                {count.toLocaleString()}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {/* Intensity legend */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Fewer
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: 180,
                        height: 8,
                        borderRadius: 999,
                        background: "linear-gradient(90deg, rgba(30,190,200,0.22), rgba(30,190,200,1))",
                    }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    More views
                </Typography>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// USHeatMap — a geographic choropleth of the United States. Each state path is
// filled by view intensity (deeper teal = more views). Geo comes back as full
// state names; we resolve those to postal abbreviations to color the map.
// ---------------------------------------------------------------------------
export function USHeatMap({ data, maxHeight = 460 }) {
    const rows = (Array.isArray(data) ? data : []).filter(
        (r) => r.key && r.key !== "Unknown" && (Number(r.count) || 0) > 0
    );

    const byAbbr = {};
    rows.forEach((r) => {
        const ab = STATE_ABBR[r.key];
        if (ab) byAbbr[ab] = (byAbbr[ab] || 0) + (Number(r.count) || 0);
    });

    const values = Object.values(byAbbr);
    const max = values.length ? Math.max(...values) : 0;

    if (max === 0) return <EmptyState label="No U.S. location data yet" />;

    const fillFor = (ab) => {
        const c = byAbbr[ab];
        if (!c) return "#eef2f4"; // no-data state
        const intensity = 0.16 + 0.84 * (c / max);
        return `rgba(30,190,200,${intensity.toFixed(3)})`;
    };

    return (
        <Box>
            <Box sx={{ width: "100%", maxWidth: 760, mx: "auto" }}>
                <svg
                    viewBox={US_VIEWBOX}
                    width="100%"
                    style={{ display: "block", height: "auto", maxHeight }}
                    role="img"
                    aria-label="United States visitor heat map by state"
                >
                    {Object.keys(US_STATE_PATHS).map((ab) => (
                        <path
                            key={ab}
                            d={US_STATE_PATHS[ab]}
                            fill={fillFor(ab)}
                            stroke="#ffffff"
                            strokeWidth={1}
                        >
                            <title>{`${ab}: ${(byAbbr[ab] || 0).toLocaleString()} views`}</title>
                        </path>
                    ))}
                </svg>
            </Box>
            {/* Intensity legend */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Fewer
                </Typography>
                <Box
                    sx={{
                        width: 180,
                        height: 8,
                        borderRadius: 999,
                        background: "linear-gradient(90deg, rgba(30,190,200,0.16), rgba(30,190,200,1))",
                    }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    More views
                </Typography>
            </Box>
        </Box>
    );
}
